import React, { useState, useMemo, useCallback } from "react";
import { OpportunityTable } from "../opportunities/OpportunityTable";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Plus,
  Calendar,
  Edit,
  AlertCircle,
  Settings,
  Loader2,
  TrendingUp,
  Building2,
  User,
  Tag,
  History,
  Trophy,
  Trash2,
} from "lucide-react";
import { parseISO } from "date-fns";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Opportunity,
  OpportunityStage,
  MoveStageData,
} from "@/services/opportunityService";
import { useAuth } from "@/hooks/useAuth";

interface OpportunityKanbanProps {
  slug: string;
  opportunities: Opportunity[];
  stages: OpportunityStage[];
  onOpportunityClick: (opportunity: Opportunity) => void;
  onCreateOpportunity: () => void;
  onStageEdit: () => void;
  onStageChange?: (opportunityId: string, newStageId: string) => void;
  view?: "kanban" | "table";
}

interface ValidationResult {
  allowed: boolean;
  reason?: string;
  opportunity?: Opportunity;
  targetStage?: string;
}

const getStageColor = (stageType: string) => {
  switch (stageType) {
    case "closed_won":
      return "bg-green-600 hover:bg-green-700";
    case "closed_lost":
      return "bg-red-500 hover:bg-red-600";
    default:
      return "bg-gray-500 hover:bg-gray-600";
  }
};

const getStageLabel = (stageType: string, stageName: string) => {
  switch (stageType) {
    case "closed_won":
      return "Ganada";
    case "closed_lost":
      return "Perdida";
    default:
      // Para etapas normales (start, progress, custom) no mostramos badge
      return null;
  }
};

const getPriorityConfig = (priority?: string) => {
  switch (priority) {
    case "large":
    case "Grande":
      return { label: "Grande", className: "bg-red-100 text-red-700" };
    case "medium":
    case "Mediana":
      return { label: "Mediana", className: "bg-yellow-100 text-yellow-700" };
    case "small":
    case "Pequeña":
      return { label: "Pequeña", className: "bg-green-100 text-green-700" };
    default:
      return null;
  }
};

const getDateInfo = (dateString: string) => {
  if (!dateString) return null;
  try {
    let date = parseISO(dateString);
    if (isNaN(date.getTime())) {
      date = new Date(dateString);
    }
    if (isNaN(date.getTime())) return null;
    const today = new Date();
    const d1 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const d2 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffTime = d1.getTime() - d2.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) {
      return {
        text: `${Math.abs(diffDays)} días retraso`,
        className: "bg-red-100 text-red-700 text-xs",
        iconClass: "text-red-600",
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};

const formatNumberWithCommas = (value: string | number): string => {
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "0,00";
  return numValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const OpportunityCard = React.memo(
  ({
    opportunity,
    index,
    stages,
    loadingId,
    currencySymbol,
    onClick,
  }: {
    opportunity: Opportunity;
    index: number;
    stages: OpportunityStage[];
    loadingId: string | null;
    currencySymbol: string;
    onClick: (opportunity: Opportunity) => void;
  }) => {
    const priorityConfig = getPriorityConfig(opportunity.priority);
    const dateInfo = getDateInfo(opportunity.estimated_close_date || "");
    const isLoading = loadingId === opportunity.id;
    const currentStage = stages.find((s) => s.id === opportunity.stage_id);
    const stageLabel = currentStage
      ? getStageLabel(currentStage.stage_type, currentStage.name)
      : null;
    const stageBadgeColor = currentStage
      ? getStageColor(currentStage.stage_type)
      : "bg-gray-500";
    const probability = opportunity.success_probability || 0;
    const progressColor =
      probability >= 75
        ? "bg-green-500"
        : probability >= 50
          ? "bg-yellow-400"
          : probability >= 25
            ? "bg-orange-400"
            : "bg-red-400";
    return (
      <Draggable draggableId={opportunity.id} index={index}>
        {(provided, snapshot) => (
          <Card
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            onClick={() => onClick(opportunity)}
            className={`cursor-pointer select-none transition-all duration-200 bg-white hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] ${
              snapshot.isDragging
                ? "shadow-xl rotate-1 opacity-95"
                : "shadow-sm"
            } ${isLoading ? "opacity-60 pointer-events-none" : ""}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="font-bold text-base text-slate-900 leading-tight line-clamp-2 flex-1">
                  {opportunity.name}
                </p>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isLoading && (
                    <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin" />
                  )}
                  {stageLabel && (
                    <span
                      className={`text-xs font-semibold text-white px-2.5 py-1 rounded-full whitespace-nowrap ${stageBadgeColor}`}
                    >
                      {stageLabel}
                    </span>
                  )}
                </div>
              </div>
              {opportunity.client_company && (
                <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-1">
                  <Building2 className="h-4 w-4 flex-shrink-0 text-slate-400" />
                  <span className="truncate font-medium text-slate-600">
                    {typeof opportunity.client_company === "string"
                      ? opportunity.client_company
                      : (opportunity.client_company as any)?.name || ""}
                  </span>
                </div>
              )}
              {opportunity.estimated_value && (
                <div className="flex items-center gap-2 mb-3 mt-2">
                  <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-lg whitespace-nowrap">
                    {currencySymbol}{" "}
                    {formatNumberWithCommas(opportunity.estimated_value)}
                  </span>
                  <div className="flex-1 bg-slate-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${progressColor}`}
                      style={{ width: `${Math.min(probability, 100)}%` }}
                    />
                  </div>
                  <span className="text-xs font-semibold text-slate-600 whitespace-nowrap flex items-center gap-0.5">
                    <TrendingUp className="h-3 w-3" />
                    {probability}%
                  </span>
                </div>
              )}
              {priorityConfig && (
                <div className="flex items-center gap-1.5 mb-3">
                  <Tag className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                  <span
                    className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${priorityConfig.className}`}
                  >
                    {priorityConfig.label}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between mt-1">
                <div className="flex items-center gap-1.5">
                  {dateInfo ? (
                    <div
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold ${dateInfo.className}`}
                    >
                      <Calendar
                        className={`h-3.5 w-3.5 ${dateInfo.iconClass}`}
                      />
                      <span>{dateInfo.text}</span>
                    </div>
                  ) : (
                    <div />
                  )}
                  {(opportunity.version_number !== undefined || opportunity.minor_version !== undefined) && (
                    <span
                      className="flex items-center gap-0.5 text-[10px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200"
                      title={`Versión ${opportunity.version_number || 1}.${opportunity.minor_version ?? 0}`}
                    >
                      <History className="h-2.5 w-2.5" />
                      V{opportunity.version_number || 1}.{opportunity.minor_version ?? 0}
                    </span>
                  )}
                </div>
                {(opportunity.contact || opportunity.creator) && (
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="text-xs font-semibold text-slate-700 leading-tight">
                        {opportunity.contact
                          ? typeof opportunity.contact === "string"
                            ? opportunity.contact
                            : (opportunity.contact as any)?.name ||
                              (opportunity.creator
                                ? `${opportunity.creator.first_name} ${opportunity.creator.last_name}`
                                : "")
                          : opportunity.creator
                            ? `${opportunity.creator.first_name} ${opportunity.creator.last_name}`
                            : ""}
                      </p>
                      <p className="text-xs text-slate-400">Contacto</p>
                    </div>
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs bg-blue-500 text-white font-bold">
                        {(opportunity.contact
                          ? typeof opportunity.contact === "string"
                            ? opportunity.contact
                            : (opportunity.contact as any)?.name ||
                              (opportunity.creator
                                ? `${opportunity.creator.first_name} ${opportunity.creator.last_name}`
                                : "") ||
                              ""
                          : (opportunity.creator
                              ? `${opportunity.creator.first_name} ${opportunity.creator.last_name}`
                              : "") || ""
                        )
                          .split(" ")[0]?.[0]
                          ?.toUpperCase() || "?"}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </Draggable>
    );
  },
);

export function OpportunityKanban({
  slug,
  opportunities = [],
  stages = [],
  onOpportunityClick,
  onCreateOpportunity,
  onStageEdit,
  onStageChange,
  view = "kanban",
}: OpportunityKanbanProps) {
  const { toast } = useToast();
  const { company, can } = useAuth();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [validationAlert, setValidationAlert] =
    useState<ValidationResult | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isLostModalOpen, setIsLostModalOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [pendingMove, setPendingMove] = useState<{
    opportunityId: string;
    destinationStageId: string;
    destinationStageName: string;
  } | null>(null);

  const [optimisticOpportunities, setOptimisticOpportunities] = useState<
    Record<string, Opportunity[]>
  >({});

  const globalCurrencySymbol = useMemo(() => {
    if ((company as any)?.currency) return (company as any).currency;
    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {}
    return "$";
  }, [company]);

  const sortedStages = useMemo(
    () => [...stages]
      .filter(s => s.stage_type !== "closed_won" && s.stage_type !== "closed_lost")
      .sort((a, b) => a.order_position - b.order_position),
    [stages],
  );
  
  const wonStage = useMemo(() => stages.find(s => s.stage_type === 'closed_won'), [stages]);
  const lostStage = useMemo(() => stages.find(s => s.stage_type === 'closed_lost'), [stages]);

  const opportunitiesByStage = useMemo(() => {
    if (Object.keys(optimisticOpportunities).length > 0)
      return optimisticOpportunities;
    return sortedStages.reduce(
      (acc, stage) => {
        acc[stage.id] = opportunities.filter(
          (opp) => opp.stage_id === stage.id,
        );
        return acc;
      },
      {} as Record<string, Opportunity[]>,
    );
  }, [sortedStages, opportunities, optimisticOpportunities]);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    const opportunity = opportunities.find((opp) => opp.id === draggableId);
    if (!opportunity) return;

    const sourceStage = stages.find((s) => s.id === source.droppableId);
    const destinationStage = stages.find(
      (s) => s.id === destination.droppableId,
    );

    // Si es etapa de pérdida, abrir modal
    if (destinationStage?.stage_type === "closed_lost") {
      setPendingMove({
        opportunityId: draggableId,
        destinationStageId: destination.droppableId,
        destinationStageName: destinationStage.name,
      });
      setIsLostModalOpen(true);
      return;
    }

    const updatedOpportunities = { ...opportunitiesByStage };

    if (updatedOpportunities[source.droppableId]) {
      updatedOpportunities[source.droppableId] = updatedOpportunities[
        source.droppableId
      ].filter((opp) => opp.id !== draggableId);
    }

    const updatedOpportunity = {
      ...opportunity,
      stage_id: destination.droppableId,
    };

    const destinationList = [
      ...(updatedOpportunities[destination.droppableId] || []),
    ];
    destinationList.splice(destination.index, 0, updatedOpportunity);
    updatedOpportunities[destination.droppableId] = destinationList;

    setOptimisticOpportunities(updatedOpportunities);
    setLoadingId(draggableId);

    try {
      const { opportunityAPI } = await import("@/services/opportunityService");

      const moveData: MoveStageData = {
        slug,
        stage_id: destination.droppableId,
      };

      await opportunityAPI.moveOpportunityStage(draggableId, moveData);

      onStageChange?.(draggableId, destination.droppableId);

      toast({
        title: "Cotización movida",
        description: "La cotización se ha movido exitosamente",
      });
    } catch (error: any) {
      setOptimisticOpportunities({});

      toast({
        title: "Error al mover cotización",
        description: error.message || "No se pudo mover la cotización",
        variant: "destructive",
      });
    } finally {
      setLoadingId(null);
    }
  };

  const handleLostConfirm = async () => {
    if (!pendingMove || !lostReason.trim()) return;

    setLoadingId(pendingMove.opportunityId);
    try {
      const { opportunityAPI } = await import("@/services/opportunityService");

      const moveData: MoveStageData = {
        slug,
        stage_id: pendingMove.destinationStageId,
        lost_reason: lostReason,
      };

      await opportunityAPI.moveOpportunityStage(pendingMove.opportunityId, moveData);

      onStageChange?.(pendingMove.opportunityId, pendingMove.destinationStageId);

      toast({
        title: "Cotización Perdida",
        description: `La cotización se ha movido a "${pendingMove.destinationStageName}"`,
      });

      setIsLostModalOpen(false);
      setLostReason("");
      setPendingMove(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo mover la cotización",
        variant: "destructive",
      });
    } finally {
      setLoadingId(null);
    }
  };

  if (!sortedStages || sortedStages.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          No hay etapas configuradas
        </h3>
        <p className="text-slate-600 text-base mb-4">
          Configura las etapas del pipeline primero
        </p>
        <Button
          onClick={onStageEdit}
          className="gap-2 bg-blue-600 hover:bg-blue-700 text-sm h-10 px-6"
          size="default"
        >
          <Settings className="h-4 w-4" />
          Configurar Etapas
        </Button>
      </div>
    );
  }

  return (
    <>
      {view === "kanban" ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex space-x-6 overflow-x-auto pb-6 scrollbar-thin scrollbar-thumb-slate-400 scrollbar-track-slate-200">
            {sortedStages.map((stage) => {
              const stageOpportunities = opportunitiesByStage[stage.id] || [];
              const totalValue = stageOpportunities.reduce(
                (sum, opp) => sum + parseFloat(opp.estimated_value || "0"),
                0,
              );

              return (
                <Droppable droppableId={stage.id} key={stage.id}>
                  {(provided, snapshot) => (
                    <div
                      className="flex-shrink-0 w-72"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      <div
                        className={`
                        rounded-xl p-4 text-white transition-all shadow-md mb-3
                        ${
                          snapshot.isDraggingOver
                            ? "ring-2 ring-blue-400 shadow-lg scale-[1.01]"
                            : ""
                        }
                      `}
                        style={{
                          backgroundColor: stage.color || "#4f46e5",
                          backgroundImage: `linear-gradient(135deg, ${
                            stage.color || "#4f46e5"
                          } 0%, ${
                            stage.color ? `${stage.color}CC` : "#4338ca"
                          } 100%)`,
                        }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className="h-2.5 w-2.5 bg-white/40 rounded-full"></div>
                            <h3 className="font-bold text-base capitalize">
                              {stage.name}
                            </h3>
                          </div>
                          <Badge
                            variant="secondary"
                            className="bg-white/30 text-white border-0 font-bold text-sm flex items-center gap-1.5"
                          >
                            <span>{stageOpportunities.length}</span>
                            <span className="text-[10px] font-normal opacity-90">
                              cotizaciones
                            </span>
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm opacity-95">
                            <span className="font-bold mr-1">Total:</span>
                            <span className="font-bold">
                              {globalCurrencySymbol}{" "}
                              {formatNumberWithCommas(totalValue)}
                            </span>
                          </div>
                          {can("stages.manage") && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 hover:bg-white/30 rounded-md text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                onStageEdit();
                              }}
                              title="Editar etapa"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>

                      <div
                        className={`
                        min-h-[500px] p-4 space-y-3 rounded-xl transition-colors duration-300
                        ${
                          snapshot.isDraggingOver
                            ? "bg-gradient-to-b from-blue-100 to-blue-200 border-2 border-dashed border-blue-400"
                            : "bg-gradient-to-b from-slate-100 to-slate-200"
                        }
                      `}
                      >
                        {stageOpportunities.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-56 text-center">
                            <div className="h-16 w-16 bg-gradient-to-br from-slate-300 to-slate-400 rounded-full flex items-center justify-center mb-4">
                              <Plus className="h-6 w-6 text-white" />
                            </div>
                            <p className="text-slate-600 text-base font-medium mb-2">
                              Sin cotizaciones
                            </p>
                            <p className="text-slate-500 text-sm">
                              Arrastra cotizaciones aquí o crea nuevas
                            </p>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={onCreateOpportunity}
                              className="mt-3 text-blue-700 hover:text-blue-800 hover:bg-blue-50 text-sm px-4"
                            >
                              Crear cotización
                            </Button>
                          </div>
                        ) : (
                          <>
                            {stageOpportunities.map((opportunity, index) => (
                              <OpportunityCard
                                key={opportunity.id}
                                opportunity={opportunity}
                                index={index}
                                stages={stages}
                                loadingId={loadingId}
                                currencySymbol={globalCurrencySymbol}
                                onClick={onOpportunityClick}
                              />
                            ))}
                            {provided.placeholder}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>

          {/* Zonas de Cierre (Won/Lost) */}
          <div className="flex gap-6 mt-8 border-t border-slate-200 pt-8 px-4">
            {wonStage && (
              <Droppable droppableId={wonStage.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex-1 h-32 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center transition-all duration-300 ${
                      snapshot.isDraggingOver
                        ? "bg-green-50 border-green-500 scale-[1.02] shadow-lg ring-4 ring-green-100"
                        : "bg-slate-50/50 border-slate-200 opacity-70"
                    }`}
                  >
                    <div className={`p-3 rounded-full mb-2 ${snapshot.isDraggingOver ? "bg-green-100 text-green-600 animate-bounce" : "bg-slate-100 text-slate-400"}`}>
                      <Trophy className="h-8 w-8" />
                    </div>
                    <span className={`font-bold text-sm uppercase tracking-wider ${snapshot.isDraggingOver ? "text-green-700" : "text-slate-500"}`}>
                      Soltar aquí para GANAR
                    </span>
                    <div className="hidden">{provided.placeholder}</div>
                  </div>
                )}
              </Droppable>
            )}

            {lostStage && (
              <Droppable droppableId={lostStage.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex-1 h-32 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center transition-all duration-300 ${
                      snapshot.isDraggingOver
                        ? "bg-red-50 border-red-500 scale-[1.02] shadow-lg ring-4 ring-red-100"
                        : "bg-slate-50/50 border-slate-200 opacity-70"
                    }`}
                  >
                    <div className={`p-3 rounded-full mb-2 ${snapshot.isDraggingOver ? "bg-red-100 text-red-600 animate-bounce" : "bg-slate-100 text-slate-400"}`}>
                      <Trash2 className="h-8 w-8" />
                    </div>
                    <span className={`font-bold text-sm uppercase tracking-wider ${snapshot.isDraggingOver ? "text-red-700" : "text-slate-500"}`}>
                      Basurero (PERDER)
                    </span>
                    <div className="hidden">{provided.placeholder}</div>
                  </div>
                )}
              </Droppable>
            )}
          </div>
        </DragDropContext>
      ) : (
        <OpportunityTable
          opportunities={opportunities}
          stages={stages}
          slug={slug}
        />
      )}

      <Dialog open={isLostModalOpen} onOpenChange={setIsLostModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Motivo de Pérdida
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <p className="text-sm text-slate-600 font-medium">
                Por favor, indica por qué se perdió esta cotización:
              </p>
              <textarea
                className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Ej: El cliente eligió a la competencia por precio..."
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
              />
            </div>
            <div className="p-3 bg-red-50 border border-red-100 rounded-md text-xs text-red-700">
              <p className="flex gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>Esta acción moverá la cotización a la etapa de <strong>{pendingMove?.destinationStageName}</strong> y requiere un motivo obligatorio.</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLostModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
                variant="destructive" 
                onClick={handleLostConfirm}
                disabled={!lostReason.trim() || !!loadingId}
            >
              Confirmar Pérdida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

